import { DEFAULT_FONT_FAMILY, type TextSpan, type TextStyle } from '@vp/shared-types';

/**
 * Shared text layout (§5.5). Layout is computed in world units at a fixed
 * reference size so line breaks are identical in WebGL2, Canvas 2D and SVG.
 */
export interface TextLine {
  text: string;
  /** Left x of the line in local coords. */
  x: number;
  /** Baseline y in local coords. */
  y: number;
  width: number;
}

export interface TextLayout {
  lines: TextLine[];
  font: string;
  fontSize: number;
  lineHeight: number;
  /** Content height (may exceed box height). */
  contentHeight: number;
  contentWidth: number;
}

export type MeasureFn = (text: string, font: string) => number;

const REF_SIZE = 100;

/** Inner text padding proportional to the font size (8 at 18 px), so text looks the same at any scale. */
export function textPadding(style: TextStyle): number {
  return style.fontSize * 0.45;
}

export function fontString(style: TextStyle, size: number): string {
  return `${style.italic ? 'italic ' : ''}${style.bold ? 'bold ' : ''}${size}px ${style.fontFamily ?? DEFAULT_FONT_FAMILY}`;
}

let measureCtx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null = null;
const widthCache = new Map<string, number>();

/** Default measurement via a 2D canvas context (browser only). */
export const canvasMeasure: MeasureFn = (text, font) => {
  const key = font + '\u0000' + text;
  const hit = widthCache.get(key);
  if (hit !== undefined) return hit;
  if (!measureCtx) {
    if (typeof OffscreenCanvas !== 'undefined') measureCtx = new OffscreenCanvas(1, 1).getContext('2d');
    else if (typeof document !== 'undefined') measureCtx = document.createElement('canvas').getContext('2d');
  }
  let w: number;
  if (measureCtx) {
    measureCtx.font = font;
    w = measureCtx.measureText(text).width;
  } else {
    w = text.length * REF_SIZE * 0.55; // headless fallback
  }
  if (widthCache.size > 50000) widthCache.clear();
  widthCache.set(key, w);
  return w;
};

const layoutCache = new Map<string, TextLayout>();
let epoch = 0;

/** Bumped whenever cached measurements become stale (a web font finished loading). */
export function textEpoch(): number {
  return epoch;
}

/** Drop measured widths and layouts, e.g. after a web font loads and replaces the fallback. */
export function resetTextCaches(): void {
  widthCache.clear();
  layoutCache.clear();
  epoch++;
}

export function layoutText(
  text: string,
  style: TextStyle,
  boxW: number,
  boxH: number,
  padding: number = textPadding(style),
  measure: MeasureFn = canvasMeasure,
): TextLayout {
  const cacheKey = measure === canvasMeasure ? `${text}\u0001${JSON.stringify(style)}\u0001${boxW}\u0001${boxH}\u0001${padding}` : '';
  if (cacheKey) {
    const hit = layoutCache.get(cacheKey);
    if (hit) return hit;
  }
  const fs = style.fontSize;
  const k = fs / REF_SIZE;
  const refFont = fontString(style, REF_SIZE);
  const mw = (s: string) => measure(s, refFont) * k;
  const lineHeight = fs * (style.lineHeight ?? 1.25);
  const maxW = Math.max(1, boxW - padding * 2);

  const out: { text: string; width: number }[] = [];
  const flush = (s: string) => {
    const t = s.trimEnd();
    out.push({ text: t, width: t ? mw(t) : 0 });
  };
  for (const para of text.split('\n')) {
    const tokens = para.match(/\S+|\s+/g) ?? [];
    let line = '';
    let wrapped = false;
    for (const tok of tokens) {
      if (/^\s/.test(tok)) {
        // drop whitespace at the start of a wrapped line, keep indentation otherwise
        if (line !== '' || !wrapped) line += tok;
        continue;
      }
      const cand = line + tok;
      if (mw(cand) <= maxW) {
        line = cand;
        continue;
      }
      if (line.trim() !== '') {
        flush(line);
        wrapped = true;
        line = '';
      }
      if (mw(tok) <= maxW) {
        line = tok;
        continue;
      }
      // a single word wider than the box: break by characters
      let chunk = '';
      for (const ch of tok) {
        if (chunk !== '' && mw(chunk + ch) > maxW) {
          flush(chunk);
          wrapped = true;
          chunk = ch;
        } else chunk += ch;
      }
      line = chunk;
    }
    flush(line);
  }

  const contentHeight = out.length * lineHeight;
  let top: number;
  if (style.valign === 'top') top = padding;
  else if (style.valign === 'bottom') top = boxH - padding - contentHeight;
  else top = (boxH - contentHeight) / 2;
  // ascent approximation: baseline at ~0.8 of the line box, centered on lineHeight
  const baselineOffset = (lineHeight - fs) / 2 + fs * 0.8;
  let contentWidth = 0;
  const lines: TextLine[] = out.map((l, i) => {
    let x: number;
    if (style.align === 'left') x = padding;
    else if (style.align === 'right') x = boxW - padding - l.width;
    else x = (boxW - l.width) / 2;
    if (l.width > contentWidth) contentWidth = l.width;
    return { text: l.text, x, y: top + i * lineHeight + baselineOffset, width: l.width };
  });
  const result: TextLayout = { lines, font: fontString(style, fs), fontSize: fs, lineHeight, contentHeight, contentWidth };
  if (cacheKey) {
    if (layoutCache.size > 5000) layoutCache.clear();
    layoutCache.set(cacheKey, result);
  }
  return result;
}

/** Height needed to fit text (for auto-growing text objects). */
export function measureTextHeight(text: string, style: TextStyle, boxW: number, padding: number = textPadding(style), measure?: MeasureFn): number {
  return layoutText(text || ' ', style, boxW, 0, padding, measure).contentHeight + padding * 2;
}

/** Width of the widest single word (words are never broken when a box can grow to fit them). */
export function longestWordWidth(text: string, style: TextStyle, measure: MeasureFn = canvasMeasure): number {
  const k = style.fontSize / REF_SIZE;
  const refFont = fontString(style, REF_SIZE);
  let max = 0;
  for (const word of text.split(/\s+/)) if (word) max = Math.max(max, measure(word, refFont) * k);
  return max;
}

/**
 * Size a shape needs so its text fits: wide enough for the longest word, tall enough for all lines.
 * `inset` > 1 leaves room for shapes whose outline is smaller than their box (ellipse, diamond).
 * Never smaller than the current size.
 */
export function fitBoxToText(text: string, style: TextStyle, w: number, h: number, inset = 1, measure: MeasureFn = canvasMeasure): { w: number; h: number } {
  if (!text.trim()) return { w, h };
  const pad = textPadding(style);
  const nw = Math.max(w, (longestWordWidth(text, style, measure) + pad * 2) * inset);
  const need = (layoutText(text, style, nw / inset, 0, pad, measure).contentHeight + pad * 2) * inset;
  return { w: nw, h: Math.max(h, need) };
}

// ---------- formatted text (spans) ----------

/** Text style of a span: the object's style with the span's own weight, slant, font, size and color. */
export function spanStyle(style: TextStyle, span: TextSpan): TextStyle {
  return {
    ...style,
    bold: span.b ? true : style.bold,
    italic: span.i ? true : style.italic,
    fontFamily: span.f ?? style.fontFamily,
    fontSize: style.fontSize * (span.k ?? 1),
    ...(span.c ? { color: span.c } : {}),
  };
}

export interface RichPiece {
  /** Text of the piece (spaces kept: a highlight covers them, like a marker). */
  text: string;
  /** Left x in local coords and width (trailing spaces at the end of a line not counted). */
  x: number;
  w: number;
  span: TextSpan;
  style: TextStyle;
}

export interface RichLine {
  pieces: RichPiece[];
  top: number;
  height: number;
  baseline: number;
  width: number;
  /** Largest font size on the line. */
  size: number;
}

export interface RichLayout {
  lines: RichLine[];
  contentHeight: number;
  contentWidth: number;
  /** Widest word (never broken when a box can grow to fit it). */
  longestWord: number;
}

const richCache = new Map<string, RichLayout>();

/**
 * Layout of formatted text, same rules as `layoutText`: words wrap at the box width (a word
 * wider than the box breaks by characters), each line is as tall as its largest font, the
 * baseline sits where plain text puts it, align and valign of the object's style apply.
 */
export function layoutRich(spans: readonly TextSpan[], style: TextStyle, boxW: number, boxH: number, padding: number = textPadding(style), measure: MeasureFn = canvasMeasure): RichLayout {
  const cacheKey = measure === canvasMeasure ? `${epoch}\u0001${JSON.stringify(spans)}\u0001${JSON.stringify(style)}\u0001${boxW}\u0001${boxH}\u0001${padding}` : '';
  if (cacheKey) {
    const hit = richCache.get(cacheKey);
    if (hit) return hit;
  }
  const lh = style.lineHeight ?? 1.25;
  const maxW = Math.max(1, boxW - padding * 2);
  const styles = new Map<TextSpan, { st: TextStyle; font: string; k: number }>();
  const look = (sp: TextSpan) => {
    let s = styles.get(sp);
    if (!s) {
      const st = spanStyle(style, sp);
      s = { st, font: fontString(st, REF_SIZE), k: st.fontSize / REF_SIZE };
      styles.set(sp, s);
    }
    return s;
  };
  const mw = (text: string, sp: TextSpan) => {
    const l = look(sp);
    return measure(text, l.font) * l.k;
  };

  type Part = { text: string; span: TextSpan };
  type Unit = { parts: Part[]; space: boolean; nl?: boolean; w: number };
  // units: a word (non-space parts that may span several fragments), a run of spaces, a line break
  const units: Unit[] = [];
  for (const sp of spans) {
    for (const tok of sp.t.match(/\n|[^\S\n]+|[^\s]+/g) ?? []) {
      if (tok === '\n') units.push({ parts: [], space: false, nl: true, w: 0 });
      else {
        const space = /^\s/.test(tok);
        const last = units[units.length - 1];
        if (last && !last.nl && last.space === space) last.parts.push({ text: tok, span: sp });
        else units.push({ parts: [{ text: tok, span: sp }], space, w: 0 });
      }
    }
  }
  let longestWord = 0;
  for (const u of units) {
    u.w = u.parts.reduce((a, p) => a + mw(p.text, p.span), 0);
    if (!u.space && !u.nl) longestWord = Math.max(longestWord, u.w);
  }

  const raw: Part[][] = [];
  let line: Part[] = [];
  let lineW = 0;
  let hasWord = false;
  let wrapped = false;
  const flush = () => {
    raw.push(line);
    line = [];
    lineW = 0;
    hasWord = false;
  };
  for (const u of units) {
    if (u.nl) {
      flush();
      wrapped = false;
      continue;
    }
    if (u.space) {
      // drop spaces at the start of a wrapped line, keep indentation otherwise
      if (hasWord || !wrapped) {
        line.push(...u.parts);
        lineW += u.w;
      }
      continue;
    }
    if (lineW + u.w <= maxW) {
      line.push(...u.parts);
      lineW += u.w;
      hasWord = true;
      continue;
    }
    if (hasWord) {
      flush();
      wrapped = true;
    }
    if (u.w <= maxW) {
      line.push(...u.parts);
      lineW = u.w;
      hasWord = true;
      continue;
    }
    // a single word wider than the box: break by characters
    lineW = 0;
    line = [];
    for (const p of u.parts) {
      let chunk = '';
      for (const ch of p.text) {
        if (chunk !== '' && lineW + mw(chunk + ch, p.span) > maxW) {
          line.push({ text: chunk, span: p.span });
          flush();
          wrapped = true;
          chunk = ch;
        } else if (chunk === '' && lineW > 0 && lineW + mw(ch, p.span) > maxW) {
          flush();
          wrapped = true;
          chunk = ch;
        } else chunk += ch;
      }
      if (chunk) {
        line.push({ text: chunk, span: p.span });
        lineW += mw(chunk, p.span);
        hasWord = true;
      }
    }
  }
  flush();

  // pieces: neighbouring parts of the same fragment, widths and heights
  const lines: RichLine[] = [];
  let y = 0;
  let contentWidth = 0;
  for (const parts of raw) {
    const pieces: RichPiece[] = [];
    for (const p of parts) {
      const last = pieces[pieces.length - 1];
      if (last && last.span === p.span) last.text += p.text;
      else pieces.push({ text: p.text, x: 0, w: 0, span: p.span, style: look(p.span).st });
    }
    let x = 0;
    let size = 0;
    for (let i = 0; i < pieces.length; i++) {
      const pc = pieces[i];
      const lastPiece = i === pieces.length - 1;
      const t = lastPiece ? pc.text.replace(/\s+$/, '') : pc.text;
      pc.x = x;
      pc.w = t ? mw(t, pc.span) : 0;
      x += lastPiece ? pc.w : mw(pc.text, pc.span);
      size = Math.max(size, pc.style.fontSize);
    }
    if (!size) size = style.fontSize;
    const height = size * lh;
    lines.push({ pieces, top: y, height, baseline: 0, width: x, size });
    contentWidth = Math.max(contentWidth, x);
    y += height;
  }
  const contentHeight = y;
  let top: number;
  if (style.valign === 'top') top = padding;
  else if (style.valign === 'bottom') top = boxH - padding - contentHeight;
  else top = (boxH - contentHeight) / 2;
  for (const l of lines) {
    const dx = style.align === 'left' ? padding : style.align === 'right' ? boxW - padding - l.width : (boxW - l.width) / 2;
    for (const pc of l.pieces) pc.x += dx;
    l.top += top;
    l.baseline = l.top + (l.height - l.size) / 2 + l.size * 0.8;
  }
  const result: RichLayout = { lines, contentHeight, contentWidth, longestWord };
  if (cacheKey) {
    if (richCache.size > 2000) richCache.clear();
    richCache.set(cacheKey, result);
  }
  return result;
}

/** Height a box needs for its text (formatted or plain). */
export function textBoxHeight(text: string, spans: readonly TextSpan[] | null, style: TextStyle, boxW: number): number {
  if (!spans) return measureTextHeight(text, style, boxW);
  const pad = textPadding(style);
  return layoutRich(spans, style, boxW, 0, pad).contentHeight + pad * 2;
}

/** Widest word of a text (formatted or plain). */
export function textLongestWord(text: string, spans: readonly TextSpan[] | null, style: TextStyle): number {
  return spans ? layoutRich(spans, style, 1e9, 0).longestWord : longestWordWidth(text, style);
}

/** `fitBoxToText` for formatted or plain text. */
export function fitBoxToContent(text: string, spans: readonly TextSpan[] | null, style: TextStyle, w: number, h: number, inset = 1): { w: number; h: number } {
  if (!spans) return fitBoxToText(text, style, w, h, inset);
  if (!text.trim()) return { w, h };
  const pad = textPadding(style);
  const nw = Math.max(w, (textLongestWord(text, spans, style) + pad * 2) * inset);
  const need = (layoutRich(spans, style, nw / inset, 0, pad).contentHeight + pad * 2) * inset;
  return { w: nw, h: Math.max(h, need) };
}
